/*
  Page interaction tracking (loaded by snippets/analytics-events.liquid).

  Every event goes to both:
  - GTM dataLayer (GA4 via GTM): { event: name, ...context, ...props }. Ecommerce events push
    { ecommerce: null } first, then { event: 'gtm_' + name, ecommerce: {...} }, as product-form.js does.
  - PostHog (posthog-js from the PixieHog app embed): posthog.capture(name, { ...context, ...props }).
    Events fired before posthog-js is ready are queued for a few seconds, then dropped.

  Context on every event comes from window.CliphairTrackingContext (set by the snippet):
  page_type, page_template, entity_id, entity_handle, plus ab_variant derived from the template
  suffix (clip-in-a -> A). page_type doubles as the entity type (collection, product, article...).

  Markup it reads (all optional, nothing happens on a page without them):
  - [data-track-section="<type>"] (+ data-track-name, from snippets/analytics-section-attributes.liquid)
    on a section root -> section_impression (>= 50% visible, or half the viewport, for 1s; once per
    section). Every event inside it carries section_type / section_name / section_id / section_position;
    id and position come from the .shopify-section wrapper, and the name falls back to the id.
  - [data-track-click="<type>"] (+ data-track-label, data-track-position) -> cta_click.
  - [data-track-list-id] / [data-track-list-name] around product cards -> the GA4 item list.
  - [data-track-item] + data-item-* on a product card -> view_item_list (batched impressions)
    and select_item. [data-track-zone] inside the card says which part was clicked; a tag
    click also fires product_card_tag_click.
  - quick-filter:toggle (section-quick-filter-rows.js) -> quick_filter_toggle.
  - slideChanged (global.js slider, doesn't bubble) -> carousel_navigate.
  - toggle on <details> in a tracked section -> faq_toggle; change in the facets form -> facet_change.
  - Scroll milestones 25/50/75/90% -> scroll_depth.

  Window API: window.CliphairAnalytics.track(name, props, { ecommerce, beacon }).
*/

(function () {
  if (window.CliphairAnalytics) return;

  const SECTION_VIEW_RATIO = 0.5;
  const SECTION_VIEW_DWELL_MS = 1000;
  const ITEM_VIEW_RATIO = 0.5;
  const ITEM_FLUSH_MS = 1000;
  const ITEM_FLUSH_SIZE = 10;
  const POSTHOG_WAIT_MS = 10000;
  const SCROLL_MILESTONES = [25, 50, 75, 90];
  const ZONE_PRIORITY = ['tag', 'swatch', 'size', 'rating', 'price', 'title', 'image'];

  /* ---------- Context ---------- */

  function getContext() {
    const ctx = window.CliphairTrackingContext || {};
    const suffix = ctx.template_suffix || '';
    const variant = suffix.match(/-([a-z])$/i);
    return clean({
      page_type: ctx.page_type,
      page_template: suffix || ctx.page_type,
      ab_variant: variant ? variant[1].toUpperCase() : undefined,
      entity_id: ctx.entity_id,
      entity_handle: ctx.entity_handle,
    });
  }

  function clean(object) {
    Object.keys(object).forEach((key) => {
      if (object[key] === undefined || object[key] === null || object[key] === '') delete object[key];
    });
    return object;
  }

  function toNumber(value) {
    if (value === undefined || value === null || value === '') return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function text(element, max = 100) {
    return element ? element.textContent.replace(/\s+/g, ' ').trim().slice(0, max) : undefined;
  }

  /* ---------- Sending ---------- */

  const posthogQueue = [];
  let posthogTimer = null;
  let posthogGaveUp = false;

  function posthogReady() {
    return window.posthog && typeof window.posthog.capture === 'function';
  }

  function sendToPostHog(name, props, beacon) {
    if (posthogReady()) {
      window.posthog.capture(name, props, beacon ? { transport: 'sendBeacon' } : undefined);
      return;
    }
    if (posthogGaveUp) return;
    posthogQueue.push([name, props]);
    if (posthogTimer) return;
    const started = Date.now();
    posthogTimer = window.setInterval(() => {
      if (posthogReady()) {
        window.clearInterval(posthogTimer);
        posthogQueue.splice(0).forEach(([queuedName, queuedProps]) => window.posthog.capture(queuedName, queuedProps));
      } else if (Date.now() - started > POSTHOG_WAIT_MS) {
        window.clearInterval(posthogTimer);
        posthogQueue.length = 0;
        posthogGaveUp = true;
      }
    }, 250);
  }

  function track(name, props = {}, opts = {}) {
    const payload = Object.assign(getContext(), clean(Object.assign({}, props)));
    window.dataLayer = window.dataLayer || [];

    if (opts.ecommerce) {
      const { items, item_list_id, item_list_name, ...rest } = payload;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(
        Object.assign({ event: 'gtm_' + name }, rest, {
          ecommerce: clean({ currency: opts.currency, item_list_id, item_list_name, items }),
        })
      );
    } else {
      window.dataLayer.push(Object.assign({ event: name }, payload));
    }

    sendToPostHog(name, payload, opts.beacon);
  }

  window.CliphairAnalytics = { track };

  /* ---------- Sections ---------- */

  function sectionWrapper(element) {
    return element.closest('.shopify-section') || element;
  }

  // Template key of the section, e.g. "help_panels" from "shopify-section-template--123__help_panels".
  function sectionKey(element) {
    const wrapper = sectionWrapper(element);
    return (wrapper.id || '').replace(/^shopify-section-/, '').replace(/^template--\d+__/, '') || undefined;
  }

  function sectionPosition(element) {
    const wrapper = sectionWrapper(element);
    if (!wrapper.parentElement) return undefined;
    const siblings = Array.from(wrapper.parentElement.children).filter((child) => child.classList.contains('shopify-section'));
    const index = siblings.indexOf(wrapper);
    return index >= 0 ? index + 1 : undefined;
  }

  function sectionProps(element) {
    const section = element && element.closest('[data-track-section]');
    if (!section) return {};
    const id = sectionKey(section);
    return {
      section_type: section.dataset.trackSection,
      section_name: section.dataset.trackName || id,
      section_id: id,
      section_position: sectionPosition(section),
    };
  }

  function initSectionViews() {
    const sections = document.querySelectorAll('[data-track-section]');
    if (sections.length === 0 || !('IntersectionObserver' in window)) return;

    const timers = new WeakMap();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          const enough =
            entry.isIntersecting &&
            (entry.intersectionRatio >= SECTION_VIEW_RATIO ||
              entry.intersectionRect.height >= window.innerHeight * SECTION_VIEW_RATIO);
          if (enough && !timers.has(element)) {
            timers.set(
              element,
              window.setTimeout(() => {
                observer.unobserve(element);
                track('section_impression', sectionProps(element));
              }, SECTION_VIEW_DWELL_MS)
            );
          } else if (!enough && timers.has(element)) {
            window.clearTimeout(timers.get(element));
            timers.delete(element);
          }
        });
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((section) => observer.observe(section));
  }

  /* ---------- Products: impressions and clicks ---------- */

  function listProps(element) {
    const list = element.closest('[data-track-list-id]');
    if (!list) return {};
    return { item_list_id: list.dataset.trackListId, item_list_name: list.dataset.trackListName };
  }

  function readItem(card) {
    const data = card.dataset;
    return clean({
      item_id: data.itemId,
      item_name: data.itemName,
      item_brand: data.itemBrand,
      item_category: data.itemCategory,
      item_category2: data.itemCategory2,
      item_category3: data.itemCategory3,
      item_variant: data.itemVariant,
      price: toNumber(data.itemPrice),
      index: toNumber(data.itemIndex),
      product_id: data.productId,
      variant_id: data.variantId,
    });
  }

  const currency = () => (window.CliphairTrackingContext || {}).currency;

  const seenItems = new WeakSet();
  const pendingItems = new Map(); // item_list_id -> { list, items: [] }
  let itemFlushTimer = null;
  let itemObserver = null;

  function flushItems(beacon) {
    window.clearTimeout(itemFlushTimer);
    itemFlushTimer = null;
    pendingItems.forEach(({ list, items }) => {
      track('view_item_list', Object.assign({}, list, { items }), { ecommerce: true, currency: currency(), beacon });
    });
    pendingItems.clear();
  }

  function queueItem(card) {
    const list = listProps(card);
    const key = list.item_list_id || '';
    if (!pendingItems.has(key)) pendingItems.set(key, { list, items: [] });
    const pending = pendingItems.get(key);
    pending.items.push(readItem(card));
    if (pending.items.length >= ITEM_FLUSH_SIZE) {
      flushItems(false);
    } else if (!itemFlushTimer) {
      itemFlushTimer = window.setTimeout(() => flushItems(false), ITEM_FLUSH_MS);
    }
  }

  function observeItems(root) {
    if (!itemObserver) return;
    const cards = root.matches && root.matches('[data-track-item]') ? [root] : root.querySelectorAll('[data-track-item]');
    cards.forEach((card) => {
      if (!seenItems.has(card)) itemObserver.observe(card);
    });
  }

  function initItemViews() {
    if (!('IntersectionObserver' in window)) return;
    itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < ITEM_VIEW_RATIO) return;
          const card = entry.target;
          itemObserver.unobserve(card);
          if (seenItems.has(card)) return;
          seenItems.add(card);
          queueItem(card);
        });
      },
      { threshold: [ITEM_VIEW_RATIO] }
    );
    observeItems(document);

    // Grid re-renders (facets.js), Load More and carousel fetches add new card nodes.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) observeItems(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    const flushOnLeave = () => flushItems(true);
    window.addEventListener('pagehide', flushOnLeave);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnLeave();
    });
  }

  // The card link's ::after overlay covers the whole card, so the click target is always the link.
  // Hit-test the pointer against the card's zones instead; the first match in ZONE_PRIORITY wins.
  function clickZone(card, event) {
    if (!event.clientX && !event.clientY) return { zone: 'card' };
    const hits = Array.from(card.querySelectorAll('[data-track-zone]')).filter((zone) => {
      const rect = zone.getBoundingClientRect();
      return (
        rect.width > 0 &&
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      );
    });
    if (hits.length === 0) return { zone: 'card' };
    hits.sort(
      (a, b) => ZONE_PRIORITY.indexOf(a.dataset.trackZone) - ZONE_PRIORITY.indexOf(b.dataset.trackZone)
    );
    return { zone: hits[0].dataset.trackZone, element: hits[0] };
  }

  function onProductClick(event, card) {
    const link = event.target.closest('a[href]');
    if (!link || !card.contains(link)) return;

    // Clicking before the impression timer fires still counts the view first.
    if (!seenItems.has(card)) {
      seenItems.add(card);
      queueItem(card);
    }
    flushItems(true);

    const item = readItem(card);
    const list = listProps(card);
    const { zone, element } = clickZone(card, event);
    const tagLabel = zone === 'tag' ? element.dataset.trackLabel || text(element) : undefined;

    track(
      'select_item',
      Object.assign({}, sectionProps(card), list, { click_zone: zone, tag_label: tagLabel, items: [item] }),
      { ecommerce: true, currency: currency(), beacon: true }
    );

    if (zone === 'tag') {
      track(
        'product_card_tag_click',
        Object.assign({}, sectionProps(card), list, item, {
          tag_label: tagLabel,
          tag_position: toNumber(element.dataset.trackPosition),
        }),
        { beacon: true }
      );
    }
  }

  /* ---------- Clicks ---------- */

  function pagedUrl(url) {
    try {
      return toNumber(new URL(url, window.location.href).searchParams.get('page'));
    } catch (error) {
      return undefined;
    }
  }

  function destination(element) {
    const link = element.closest('a[href]') || element.querySelector('a[href]');
    if (!link) return undefined;
    try {
      const url = new URL(link.getAttribute('href'), window.location.href);
      return url.origin === window.location.origin ? url.pathname + url.search : url.href;
    } catch (error) {
      return link.getAttribute('href');
    }
  }

  const lastArrow = new WeakMap(); // slider -> { direction, time }

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const card = target.closest('[data-track-item]');
    if (card) {
      onProductClick(event, card);
      return;
    }

    const arrow = target.closest('button[name="next"], button[name="previous"]');
    const slider = arrow && arrow.closest('slider-component-custom, slider-component');
    if (slider) {
      lastArrow.set(slider, { direction: arrow.name, time: Date.now() });
      return;
    }

    const cta = target.closest('[data-track-click]');
    if (!cta) return;
    const type = cta.dataset.trackClick;
    const url = destination(cta);
    track(
      'cta_click',
      Object.assign(sectionProps(cta), {
        element_type: type,
        element_label: cta.dataset.trackLabel || text(cta),
        element_position: toNumber(cta.dataset.trackPosition),
        destination_url: url,
        page: type === 'load_more' ? pagedUrl(cta.dataset.nextUrl) : undefined,
      }),
      { beacon: Boolean(url) }
    );
  }

  /* ---------- Quick filter chips ---------- */

  function isPressed(chip) {
    return chip.getAttribute('aria-pressed') === 'true' || chip.classList.contains('is-selected');
  }

  function rowLabel(row) {
    const id = row.getAttribute('aria-labelledby');
    return text(id ? document.getElementById(id) : null);
  }

  // "colour_group:Blondes,Browns|product_type:Clip-in", flat so GA4 can take it as one parameter.
  function activeFilters(root) {
    return Array.from(root.querySelectorAll('[data-row-kind]'))
      .map((row) => {
        const labels = Array.from(row.querySelectorAll('a[data-chip]'))
          .filter((chip) => isPressed(chip) && !chip.hasAttribute('data-chip-all'))
          .map((chip) => chip.dataset.label || text(chip));
        return labels.length ? `${row.dataset.rowKind}:${labels.join(',')}` : null;
      })
      .filter(Boolean)
      .join('|');
  }

  function visibleRanges() {
    return Array.from(document.querySelectorAll('collection-range-carousel[data-range-handle]'))
      .filter((range) => {
        const wrapper = sectionWrapper(range);
        return !wrapper.classList.contains('quick-filter-rows-hidden') && !wrapper.classList.contains('collection-range-carousel-empty');
      })
      .map((range) => range.dataset.rangeHandle);
  }

  function onQuickFilterToggle(event) {
    const { chip, row, selected } = event.detail || {};
    const root = event.target;
    if (!chip || !row || !root) return;
    const rows = Array.from(root.querySelectorAll('[data-row-kind]'));
    const chips = Array.from(row.querySelectorAll('a[data-chip]'));
    const mode = root.dataset.mode;
    const filters = activeFilters(root);

    track(
      'quick_filter_toggle',
      Object.assign(sectionProps(root), {
        action: selected ? 'select' : 'deselect',
        mode,
        row_kind: row.dataset.rowKind,
        row_param: row.dataset.rowParam,
        row_label: rowLabel(row),
        row_position: rows.indexOf(row) + 1,
        chip_key: chip.dataset.chipKey,
        chip_label: chip.dataset.label || text(chip),
        chip_position: chips.indexOf(chip) + 1,
        is_all_chip: chip.hasAttribute('data-chip-all'),
        active_filters: filters,
        active_filter_count: root.querySelectorAll('[data-row-kind] a[data-chip][aria-pressed="true"]:not([data-chip-all])').length,
        visible_ranges: mode === 'ranges' ? visibleRanges().join(',') : undefined,
      })
    );
  }

  /* ---------- Carousels ---------- */

  const lastPage = new WeakMap(); // slider -> page
  const slideTimers = new WeakMap();

  function onSlideChanged(event) {
    const slider = event.target;
    if (!(slider instanceof Element)) return;
    const range = slider.closest('[data-track-section]');
    if (!range) return;

    window.clearTimeout(slideTimers.get(slider));
    slideTimers.set(
      slider,
      window.setTimeout(() => {
        const page = toNumber(event.detail && event.detail.currentPage);
        const previous = lastPage.get(slider) || 1;
        if (page === undefined || page === previous) return;
        lastPage.set(slider, page);
        const arrow = lastArrow.get(slider);
        const byArrow = arrow && Date.now() - arrow.time < 1500;
        const list = listProps(range);

        track(
          'carousel_navigate',
          Object.assign(sectionProps(range), {
            range_handle: range.dataset.rangeHandle || list.item_list_id,
            range_title: list.item_list_name,
            direction: page > previous ? 'next' : 'previous',
            method: byArrow ? 'arrow' : 'swipe',
            page,
          })
        );
      }, 400)
    );
  }

  /* ---------- FAQ and facets ---------- */

  function onToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const section = details.closest('[data-track-section]');
    if (!section) return;
    const all = Array.from(section.querySelectorAll('details'));
    track(
      'faq_toggle',
      Object.assign(sectionProps(section), {
        question: text(details.querySelector('summary'), 150),
        position: all.indexOf(details) + 1,
      })
    );
  }

  function onFacetChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.closest('facet-filters-form')) return;
    if (input.name === 'sort_by') {
      track('facet_change', Object.assign(sectionProps(input), { sort_by: input.value }));
      return;
    }
    if (!input.name || !input.name.startsWith('filter.')) return;
    const checkable = input.type === 'checkbox' || input.type === 'radio';
    track(
      'facet_change',
      Object.assign(sectionProps(input), {
        filter_param: input.name,
        value: input.value,
        action: checkable ? (input.checked ? 'select' : 'deselect') : 'set',
      })
    );
  }

  /* ---------- Scroll depth ---------- */

  function initScrollDepth() {
    const fired = new Set();
    let ticking = false;

    function sectionAtBottom() {
      const bottom = window.innerHeight;
      let last = null;
      document.querySelectorAll('[data-track-section]').forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.height > 0 && rect.top < bottom) last = section;
      });
      return last;
    }

    function check() {
      ticking = false;
      const scrollable = document.documentElement.scrollHeight;
      if (!scrollable) return;
      const percent = ((window.scrollY + window.innerHeight) / scrollable) * 100;
      SCROLL_MILESTONES.forEach((milestone) => {
        if (percent >= milestone && !fired.has(milestone)) {
          fired.add(milestone);
          const section = sectionProps(sectionAtBottom() || document.body);
          track('scroll_depth', {
            percent: milestone,
            last_section_type: section.section_type,
            last_section_name: section.section_name,
          });
        }
      });
      if (fired.size === SCROLL_MILESTONES.length) window.removeEventListener('scroll', onScroll);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Init ---------- */

  function init() {
    initSectionViews();
    initItemViews();
    initScrollDepth();
    // Capture phase: runs before any handler that stops propagation, and catches non-bubbling events.
    document.addEventListener('click', onClick, true);
    document.addEventListener('slideChanged', onSlideChanged, true);
    document.addEventListener('toggle', onToggle, true);
    document.addEventListener('change', onFacetChange, true);
    document.addEventListener('quick-filter:toggle', onQuickFilterToggle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
